// In dev the Vite proxy forwards `/api` to localhost:4000 so a relative path works.
// In prod the static site is served from megarsvp.com and the API lives on
// api.megarsvp.com — VITE_API_URL is baked in at build time.
const API_BASE_URL = `${import.meta.env.VITE_API_URL || ''}/api`;

/** Absolute URL for an API path (with /api prefix already included). Use this
 *  for raw fetch() calls (e.g. multipart uploads where apiPost can't help). */
export const apiUrl = (path: string) =>
  `${import.meta.env.VITE_API_URL || ''}${path.startsWith('/') ? path : `/${path}`}`;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    let message: string = errorData.message || response.statusText;
    // Surface Zod field-level errors so toasts say which field is bad.
    if (errorData.details && typeof errorData.details === 'object') {
      const fieldErrors = Object.entries(errorData.details as Record<string, string[]>)
        .map(([field, errs]) => `${field}: ${Array.isArray(errs) ? errs.join(', ') : errs}`)
        .join('; ');
      if (fieldErrors) message = `${message} — ${fieldErrors}`;
    }
    throw new ApiError(response.status, message, errorData);
  }
  // Handle 204 No Content responses (common for DELETE)
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json();
}

// Refresh also supports older sessions whose access token had a shorter lifetime.
// Dedupe concurrent refreshes so a burst of 401s triggers a single /auth/refresh.
let refreshPromise: Promise<boolean> | null = null;
function tryRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
      .then((r) => r.ok)
      .catch(() => false);
    void refreshPromise.finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function redirectToLogin() {
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

async function request<T>(endpoint: string, init: RequestInit, allowRetry = true): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    credentials: 'include',
    ...init,
  });

  // On an expired access token, transparently refresh once and retry. If the
  // refresh token is also gone, the session is over → send them to login.
  if (response.status === 401 && allowRetry && endpoint !== '/auth/refresh') {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return request<T>(endpoint, init, false);
    }
    // Refresh failed. Do NOT bounce anonymous visitors: public pages (RSVP,
    // register, photo upload) probe /auth/me while logged out — that 401 is
    // expected and must not redirect. Only real authenticated calls redirect.
    if (endpoint !== '/auth/me') {
      redirectToLogin();
    }
  }

  return handleResponse<T>(response);
}

const jsonHeaders = { 'Content-Type': 'application/json' };

export async function apiGet<T>(endpoint: string): Promise<T> {
  return request<T>(endpoint, { method: 'GET', headers: jsonHeaders });
}

export async function apiPost<T>(endpoint: string, data?: unknown): Promise<T> {
  return request<T>(endpoint, {
    method: 'POST',
    headers: jsonHeaders,
    body: data ? JSON.stringify(data) : undefined,
  });
}

export async function apiPatch<T>(endpoint: string, data: unknown): Promise<T> {
  return request<T>(endpoint, {
    method: 'PATCH',
    headers: jsonHeaders,
    body: JSON.stringify(data),
  });
}

export async function apiPut<T>(endpoint: string, data: unknown): Promise<T> {
  return request<T>(endpoint, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(data),
  });
}

export async function apiDelete<T>(endpoint: string): Promise<T> {
  return request<T>(endpoint, { method: 'DELETE' });
}
